import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface CompanyInfo {
  id: number
  company_name: string | null
  zipcode: string | null
  address: string | null
  phone: string | null
  operating_hours: string | null
  email: string | null
  updated_at: string
}

interface CompanyForm {
  company_name: string
  zipcode: string
  address: string
  phone: string
  operating_hours: string
  email: string
}

const EMPTY_FORM: CompanyForm = {
  company_name: '',
  zipcode: '',
  address: '',
  phone: '',
  operating_hours: '',
  email: '',
}

const inputClass =
  'border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors w-full'

export default function CompanyInfoTab() {
  const [info, setInfo] = useState<CompanyInfo | null>(null)
  const [form, setForm] = useState<CompanyForm>(EMPTY_FORM)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchInfo()
  }, [])

  const fetchInfo = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/company/detail'))
      if (!res.ok) throw new Error('조회 실패')
      const data: CompanyInfo = await res.json()
      setInfo(data)
      setForm({
        company_name: data.company_name ?? '',
        zipcode: data.zipcode ?? '',
        address: data.address ?? '',
        phone: data.phone ?? '',
        operating_hours: data.operating_hours ?? '',
        email: data.email ?? '',
      })
    } catch {
      alert('기관 정보를 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (key: keyof CompanyForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    if (!window.confirm('기관 정보를 저장하시겠습니까?')) return
    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/company/admin/detail'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: form.company_name !== '' ? form.company_name : null,
          zipcode: form.zipcode !== '' ? form.zipcode : null,
          address: form.address !== '' ? form.address : null,
          phone: form.phone !== '' ? form.phone : null,
          operating_hours: form.operating_hours !== '' ? form.operating_hours : null,
          email: form.email !== '' ? form.email : null,
        }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }
      const result = await res.json()
      alert(result.message ?? '저장되었습니다.')
      fetchInfo()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">
        불러오는 중...
      </div>
    )
  }

  return (
    <div className="max-w-[720px]">
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <h2 className="text-[14px] font-bold text-[#5a3d32] mb-1">기관 정보</h2>
        <p className="text-[12px] text-gray-400 mb-6">
          홈페이지와 키오스크에 노출되는 기관 기본 정보를 관리합니다.
        </p>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#5a3d32] font-medium">기관명</label>
            <input
              type="text"
              value={form.company_name}
              onChange={e => handleChange('company_name', e.target.value)}
              placeholder="예: 마루빛"
              className={inputClass}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex flex-col gap-1 w-[160px] shrink-0">
              <label className="text-[12px] text-[#5a3d32] font-medium">우편번호</label>
              <input
                type="text"
                value={form.zipcode}
                onChange={e => handleChange('zipcode', e.target.value)}
                placeholder="예: 31119"
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-[12px] text-[#5a3d32] font-medium">주소</label>
              <input
                type="text"
                value={form.address}
                onChange={e => handleChange('address', e.target.value)}
                placeholder="예: 충청남도 천안시 서북구 ○○로 12"
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-[12px] text-[#5a3d32] font-medium">전화번호</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="예: 041-000-0000"
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-[12px] text-[#5a3d32] font-medium">이메일</label>
              <input
                type="text"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="예: marubit@example.com"
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#5a3d32] font-medium">
              이용시간
              <span className="text-gray-400 font-normal ml-2">(홈페이지 노출 문구)</span>
            </label>
            <input
              type="text"
              value={form.operating_hours}
              onChange={e => handleChange('operating_hours', e.target.value)}
              placeholder="예: 09:00 ~ 18:00"
              className={inputClass}
            />
          </div>
        </div>

        <div className="border-t border-[#f5f2f0] mt-6 pt-5 flex items-center justify-between">
          <p className="text-[11px] text-gray-400 m-0">
            {info && (
              <>
                최종 수정:{' '}
                <span className="text-[#887267] font-semibold">
                  {new Date(info.updated_at).toLocaleString('ko-KR')}
                </span>
              </>
            )}
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-none"
          >
            {saving ? '저장 중...' : '저장하기'}
          </button>
        </div>
      </div>
    </div>
  )
}
